import { FieldProps } from "@rjsf/utils";
import { FormattedMessage } from "react-intl";

import { objectFromEntries, objectKeys } from "@nens/shared-utils";

import { getSchemaDefaultValues } from "@/features/project-process-run";
import { RanaTab } from "@/lib/rana-tabs";

export function useProcessRunFormTabs(props: FieldProps) {
  const { schema, uiSchema, formData, onChange, readonly } = props;

  const subSchemas = (schema.anyOf?.length ? schema.anyOf : schema.oneOf)?.filter(
    (schema): schema is NonNullable<FieldProps["schema"]> => typeof schema !== "boolean"
  );
  const subUiSchemas = (uiSchema?.anyOf?.length ? uiSchema.anyOf : uiSchema?.oneOf)?.filter(
    (uiSchema: FieldProps["uiSchema"]): uiSchema is NonNullable<FieldProps["uiSchema"]> =>
      typeof uiSchema !== "boolean"
  );

  const { SchemaField } = props.registry.fields;

  const tabs: Record<string, RanaTab> | undefined =
    subSchemas && subUiSchemas
      ? objectFromEntries(
          subSchemas.map((schema, idx) => {
            const key = schema?.title + "";
            const uiSchema = subUiSchemas[idx];
            const title = schema?.title || (
              <FormattedMessage id="process.no_title_given" defaultMessage="No title given" />
            );
            const panel = (
              <SchemaField
                {...props}
                uiSchema={uiSchema}
                // Hide schema and name so that it doesn't show a title
                name=""
                schema={{ ...schema, title: "" }}
              />
            );
            const tab: RanaTab = !readonly
              ? {
                  title,
                  panel,
                }
              : {
                  title,
                  panel: null,
                  disabledPanel: panel,
                };
            return [key, tab];
          })
        )
      : undefined;

  const onTabChange = (index: number) => {
    const property = subSchemas?.[index];

    onChange(getSchemaDefaultValues(property));
  };

  const foundTabIndex =
    subSchemas?.findIndex((schema) => {
      if (schema.type === "object")
        return (
          formData &&
          objectKeys(formData).every(
            (key) =>
              schema.properties &&
              typeof schema.properties !== "boolean" &&
              objectKeys(schema.properties).includes(String(key))
          )
        );

      if (typeof formData === schema.type) return true;

      return false;
    }) ?? 0;
  const defaultTab = foundTabIndex >= 0 ? foundTabIndex : 0;

  return { tabs, defaultTab, onTabChange };
}
